// Copyright ©️ 2026 eVolve MEP, LLC

import { useState } from 'react';

import { type StrongOmit, type Facility, type FacilityCreate, useEvolvePost } from '@evolvemep/api-ts';
import { Button, Flex, NumberInput, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { FormModal, useFormModal } from 'components/Mantine/Modals/FormModal';

type Props = {
  editing: boolean;
  onFacilityCreated: (facility: Facility) => void;
  onClose: () => void;
};

type FormBody = StrongOmit<FacilityCreate, 'deliveryLeadTime'> & {
  deliveryLeadTime: number | '';
};

const CreateFacilityForm = ({ onFacilityCreated }: Pick<Props, 'onFacilityCreated'>) => {
  const { apiCall: createCall } = useEvolvePost('shop/facility');

  const form = useForm<FormBody>({
    initialValues: {
      facilityName: '',
      deliveryLeadTime: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'United States',
    },
    validate: {
      facilityName: (n) => !n,
    },
  });

  const onSubmit = async ({ deliveryLeadTime, ...values }: typeof form.values) => {
    await createCall({
      ...values,
      deliveryLeadTime: deliveryLeadTime || 0,
    }).then((facility) => {
      onFacilityCreated(facility);
      notifications.show({
        title: 'Successfully created',
        message: `Facility ${facility.facilityName} created.`,
        color: 'green',
      });
    });
  };

  useFormModal(form, onSubmit);

  return (
    <>
      <Flex gap="md">
        <TextInput
          label="Facility Name"
          autoFocus
          data-autofocus
          w="100%"
          maxLength={64}
          withAsterisk
          {...form.getInputProps('facilityName')}
        />
        <NumberInput
          label="Delivery Lead Time"
          rightSection={
            <Text c="dimmed" fz="sm" style={{ wordBreak: 'initial' }}>
              days
            </Text>
          }
          allowDecimal={false}
          rightSectionProps={{ style: { marginRight: '10px' } }}
          min={0}
          {...form.getInputProps('deliveryLeadTime')}
        />
      </Flex>
      <TextInput label="Address Line 1" name="Address Line 1" {...form.getInputProps('addressLine1')} />
      <TextInput label="Address Line 2" name="Address Line 2" {...form.getInputProps('addressLine2')} />
      <TextInput label="City" name="City" {...form.getInputProps('city')} />
      <Flex gap="md">
        <TextInput style={{ width: '100%' }} label="State" name="State" {...form.getInputProps('state')} />
        <TextInput label="Zip/Postal Code" name="Zip/Postal Code" {...form.getInputProps('postalCode')} />
      </Flex>
      <TextInput label="Country" name="Country" disabled {...form.getInputProps('country')} />
    </>
  );
};

export const CreateFacilityModal = (props: Omit<Props, 'onClose'>) => {
  const [opened, setOpened] = useState(false);

  return (
    <>
      <Button
        onClick={() => {
          setOpened(true);
        }}
        disabled={props.editing}
        leftSection={<EvolveIcon icon="Add" size="sm" />}
      >
        New facility
      </Button>
      <FormModal
        size="lg"
        title="New facility"
        opened={opened}
        onClose={() => setOpened(false)}
        confirmationText="Create"
        type="submit"
      >
        <CreateFacilityForm {...props} />
      </FormModal>
    </>
  );
};
